import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute ';
import AdminRoute from './components/AdminRoute';
import CoachRoute from './components/CoachRoute';
import DoctorRoute from './components/DoctorRoute';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';

// Main Site Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import CoachManagement from './pages/admin/CoachManagement';
import ClientManagement from './pages/admin/ClientManagement';
import DoctorManagement from './pages/admin/DoctorManagement';
import CoachDashboard from './pages/coach/CoachDashboard';
import ClientProgress from './pages/coach/ClientProgress';
import DoctorDashboard from './pages/doctor/DoctorDashboard';
import DoctorClientProgress from './pages/doctor/DoctorClientProgress';
import OurProgram from './pages/OurProgram';
import SuccessStories from './pages/SuccessStories';
import Blog from './pages/Blog';
import BlogDetail from './pages/BlogDetail';
import AddBlog from './pages/AddBlog';
import FAQ from './pages/FAQ';
import Pricing from './pages/Pricing';
import ProgramCheckout from './pages/ProgramCheckout';
import AboutUs from './pages/AboutUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import FreeResources from './pages/FreeResources';
import FitnessCalculators from './pages/calculators/FitnessCalculators';
import BMICalculator from './pages/calculators/BMICalculator';
import TDEECalculator from './pages/calculators/TDEECalculator';
import MacroCalculator from './pages/calculators/MacroCalculator';
import WeightLossTimeline from './pages/calculators/WeightLossTimeline';
import IdealBodyWeight from './pages/calculators/IdealBodyWeight';
import WaterIntakeCalculator from './pages/calculators/WaterIntakeCalculator';

// GLP-1 Site Pages
import GLP1Layout from './components/glp1/GLP1Layout';
import GLP1Home from './pages/glp1/GLP1Home';
import GLP1Eligibility from './pages/glp1/GLP1Eligibility';
import GLP1Result from './pages/glp1/GLP1Result';
import GLP1Research from './pages/glp1/GLP1Research';
import GLP1FAQ from './pages/glp1/GLP1FAQ';
import GLP1Terms from './pages/glp1/GLP1Terms';
import GLP1MedicalDisclaimer from './pages/glp1/GLP1MedicalDisclaimer';
import GLP1Contact from './pages/glp1/GLP1Contact';

// Utility to detect subdomain
const getSubdomain = () => {
  const hostname = window.location.hostname;
  // Handle localhost for development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check URL params for testing: ?subdomain=glp1
    const params = new URLSearchParams(window.location.search);
    return params.get('subdomain') || 'main';
  }
  // Production: extract subdomain
  const parts = hostname.split('.');
  if (parts.length > 2) {
    return parts[0]; // e.g., 'glp1' from 'glp1.fitnession.fit'
  }
  return 'main';
};

// Main Site Routes
const MainRoutes = () => (
  <Routes>
    {/* Public Routes with Layout */}
    <Route path="/" element={<Layout><Landing /></Layout>} />
    <Route path="/our-program" element={<Layout><OurProgram /></Layout>} />
    <Route path="/success-stories" element={<Layout><SuccessStories /></Layout>} />
    <Route path="/blog" element={<Layout><Blog /></Layout>} />
    <Route path="/blog/:slug" element={<Layout showFooter={false}><BlogDetail /></Layout>} />
    <Route path="/faq" element={<Layout><FAQ /></Layout>} />
    <Route path="/pricing" element={<Layout><Pricing /></Layout>} />
    <Route path="/about" element={<Layout><AboutUs /></Layout>} />
    <Route path="/contact" element={<Layout><Contact /></Layout>} />
    <Route path="/free-resources" element={<Layout><FreeResources /></Layout>} />
    <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
    <Route path="/terms" element={<Layout><Terms /></Layout>} />

    {/* Calculator Routes */}
    <Route path="/calculators" element={<Layout><FitnessCalculators /></Layout>} />
    <Route path="/calculators/bmi" element={<Layout><BMICalculator /></Layout>} />
    <Route path="/calculators/tdee" element={<Layout><TDEECalculator /></Layout>} />
    <Route path="/calculators/macro" element={<Layout><MacroCalculator /></Layout>} />
    <Route path="/calculators/weight-loss-timeline" element={<Layout><WeightLossTimeline /></Layout>} />
    <Route path="/calculators/ideal-weight" element={<Layout><IdealBodyWeight /></Layout>} />
    <Route path="/calculators/water-intake" element={<Layout><WaterIntakeCalculator /></Layout>} />

    {/* Auth Routes without Footer */}
    <Route path="/login" element={<Layout showFooter={false}><Login /></Layout>} />
    <Route path="/register" element={<Layout showFooter={false}><Register /></Layout>} />
    <Route path="/forgot-password" element={<Layout showFooter={false}><ForgotPassword /></Layout>} />
    <Route path="/reset-password" element={<Layout showFooter={false}><ResetPassword /></Layout>} />

    {/* Protected Routes */}
    <Route
      path="/dashboard"
      element={
        <ProtectedRoute>
          <Layout showFooter={false}><Dashboard /></Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/checkout/:programId"
      element={
        <ProtectedRoute>
          <Layout showFooter={false}><ProgramCheckout /></Layout>
        </ProtectedRoute>
      }
    />
    {/* Blog Admin Routes */}
    <Route
      path="/blog/new"
      element={
        <AdminRoute>
          <Layout showFooter={false}><AddBlog /></Layout>
        </AdminRoute>
      }
    />
    <Route
      path="/blog/edit/:id"
      element={
        <AdminRoute>
          <Layout showFooter={false}><AddBlog /></Layout>
        </AdminRoute>
      }
    />

    {/* Admin Routes */}
    <Route
      path="/admin"
      element={
        <AdminRoute>
          <Layout showFooter={false}><AdminDashboard /></Layout>
        </AdminRoute>
      }
    />
    <Route
      path="/admin/coaches"
      element={
        <AdminRoute>
          <Layout showFooter={false}><CoachManagement /></Layout>
        </AdminRoute>
      }
    />
    <Route
      path="/admin/clients"
      element={
        <AdminRoute>
          <Layout showFooter={false}><ClientManagement /></Layout>
        </AdminRoute>
      }
    />
    <Route
      path="/admin/doctors"
      element={
        <AdminRoute>
          <Layout showFooter={false}><DoctorManagement /></Layout>
        </AdminRoute>
      }
    />

    {/* Coach Routes */}
    <Route
      path="/coach-dashboard"
      element={
        <CoachRoute>
          <Layout showFooter={false}><CoachDashboard /></Layout>
        </CoachRoute>
      }
    />
    <Route
      path="/coach-dashboard/client/:clientId"
      element={
        <CoachRoute>
          <Layout showFooter={false}><ClientProgress /></Layout>
        </CoachRoute>
      }
    />

    {/* Doctor Routes */}
    <Route
      path="/doctor-dashboard"
      element={
        <DoctorRoute>
          <Layout showFooter={false}><DoctorDashboard /></Layout>
        </DoctorRoute>
      }
    />
    <Route
      path="/doctor-dashboard/patient/:clientId"
      element={
        <DoctorRoute>
          <Layout showFooter={false}><DoctorClientProgress /></Layout>
        </DoctorRoute>
      }
    />

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

// GLP-1 Site Routes
const GLP1Routes = () => (
  <Routes>
    <Route path="/" element={<GLP1Layout><GLP1Home /></GLP1Layout>} />
    <Route path="/eligibility" element={<GLP1Layout><GLP1Eligibility /></GLP1Layout>} />
    <Route path="/result" element={<GLP1Layout><GLP1Result /></GLP1Layout>} />
    <Route path="/research" element={<GLP1Layout><GLP1Research /></GLP1Layout>} />
    <Route path="/faq" element={<GLP1Layout><GLP1FAQ /></GLP1Layout>} />
    <Route path="/privacy-policy" element={<GLP1Layout><PrivacyPolicy /></GLP1Layout>} />
    <Route path="/terms" element={<GLP1Layout><GLP1Terms /></GLP1Layout>} />
    <Route path="/medical-disclaimer" element={<GLP1Layout><GLP1MedicalDisclaimer /></GLP1Layout>} />
    <Route path="/contact" element={<GLP1Layout><GLP1Contact /></GLP1Layout>} />

    {/* Blog routes on GLP-1 subdomain — shows only GLP-1 category articles */}
    <Route path="/blog" element={<GLP1Layout><Blog glp1Mode={true} /></GLP1Layout>} />
    <Route path="/blog/:slug" element={<GLP1Layout showFooter={false}><BlogDetail /></GLP1Layout>} />
    <Route
      path="/blog/new"
      element={
        <AdminRoute>
          <GLP1Layout showFooter={false}><AddBlog /></GLP1Layout>
        </AdminRoute>
      }
    />
    <Route
      path="/blog/edit/:id"
      element={
        <AdminRoute>
          <GLP1Layout showFooter={false}><AddBlog /></GLP1Layout>
        </AdminRoute>
      }
    />

    {/* Auth routes - shared with main site */}
    <Route path="/login" element={<GLP1Layout showFooter={false}><Login /></GLP1Layout>} />
    <Route path="/register" element={<GLP1Layout showFooter={false}><Register /></GLP1Layout>} />

    {/* Checkout for GLP-1 */}
    <Route
      path="/checkout/:programId"
      element={
        <ProtectedRoute>
          <GLP1Layout showFooter={false}><ProgramCheckout /></GLP1Layout>
        </ProtectedRoute>
      }
    />

    {/* Dashboard redirect to main site */}
    <Route path="/dashboard" element={<Navigate to="https://fitnession.fit/dashboard" replace />} />

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

function App() {
  const subdomain = getSubdomain();
  const isGLP1Site = subdomain === 'glp1';

  return (
    <Router>
      <AuthProvider>
        <ScrollToTop />
        <div className="App">
          {isGLP1Site ? <GLP1Routes /> : <MainRoutes />}
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
